import type {
  CheckoutInitTokenTransferResponse,
  FunAddress,
} from '@funkit/api-base'
import { bitcoinChain, solanaChain, tronChain } from '@funkit/chains'
import {
  type QRCodeUri,
  type TransferTokenQrCodeType,
  getDepositAddressForChain,
  getTransferTokenQrCodeUri,
} from '@funkit/connect-core'

import { useTransferInit } from './useTransferInit'
import type { DepositTargetConfig } from '~/flow/depositFlowConfig'

// Web applies a per-customer slippage matrix; the thin RN flow uses a single
// default (web's fallback value) until a customer needs otherwise.
const DEFAULT_MAX_SLIPPAGE_PERCENT = 0.25

function resolveBlockchain(
  chainId: number | undefined,
): TransferTokenQrCodeType {
  if (chainId === solanaChain.id) {
    return 'solana'
  }
  if (chainId === bitcoinChain.id) {
    return 'bitcoin'
  }
  if (chainId === tronChain.id) {
    return 'tron'
  }
  return 'ethereum'
}

export interface UseTokenTransferParams {
  checkoutConfig: DepositTargetConfig | undefined
  /** Chain the user picked to send funds on. */
  selectedChainId: number | undefined
  dynamicRoutingId?: string
  /** Active flow's checkoutId — see {@link useTransferInit}. */
  checkoutId?: string
  /** Host-supplied correlation ids — see {@link useTransferInit}. */
  clientMetadata?: NonNullable<object>
  enabled?: boolean
}

export interface UseTokenTransferResult {
  /** Where the user sends funds first. */
  depositAddress: FunAddress | undefined
  /** Raw deposit-init response (per-chain addresses); gates which chains show. */
  transferInit: CheckoutInitTokenTransferResponse | undefined
  /** Final destination of the funds. */
  recipientAddr: string
  /** Default max slippage, as a percent. */
  maxSlippagePercent: number
  qrCodeUri: QRCodeUri | undefined
  blockchain: TransferTokenQrCodeType
  isLoading: boolean
  /** The deposit-address lookup failed; nothing can be delivered without it. */
  depositAddressError: Error | undefined
  /** Replays the deposit-address lookup. */
  retryDepositAddress: () => void
}

/**
 * Thin RN reimplementation of `@funkit/connect`'s `useTokenTransfer`: resolves
 * the deposit address + QR URI for the selected chain. The web hook's i18n
 * tooltips, fiat conversion, price-impact estimate, per-customer slippage, and
 * Polygon "original recipient" special-case are intentionally omitted. (The
 * per-chain minimums are computed in `buildAvailableChains`, not here.)
 */
export function useTokenTransfer({
  checkoutConfig,
  selectedChainId,
  dynamicRoutingId,
  checkoutId,
  clientMetadata,
  enabled,
}: UseTokenTransferParams): UseTokenTransferResult {
  const { transferInit, recipientAddr, isLoading, error, retry } =
    useTransferInit({
      checkoutConfig,
      dynamicRoutingId,
      checkoutId,
      clientMetadata,
      enabled,
    })

  const depositAddress =
    selectedChainId !== undefined
      ? getDepositAddressForChain(selectedChainId, transferInit)
      : undefined

  const blockchain = resolveBlockchain(selectedChainId)

  const qrCodeUri = depositAddress
    ? getTransferTokenQrCodeUri({
        depositAddress,
        type: blockchain,
        chainId: selectedChainId,
      })
    : undefined

  return {
    depositAddress,
    transferInit,
    recipientAddr,
    maxSlippagePercent: DEFAULT_MAX_SLIPPAGE_PERCENT,
    qrCodeUri,
    blockchain,
    isLoading,
    depositAddressError: error,
    retryDepositAddress: retry,
  }
}
